import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "w-100 d-flex justify-content-start align-items-center" }
const _hoisted_2 = { class: "datepicker-label" }
const _hoisted_3 = { class: "input-title datepicker-input-label" }
const _hoisted_4 = { class: "d-flex justify-content-around align-items-center m-2 border-top" }
const _hoisted_5 = { class: "input-title fw-bold m-2" }
const _hoisted_6 = { class: "datepicker-header-sticky" }
const _hoisted_7 = { class: "d-flex justify-content-end w-100 p-3" }
const _hoisted_8 = { class: "datepicker-calendar-content" }
const _hoisted_9 = { class: "datepicker-footer-sticky" }
const _hoisted_10 = { class: "d-flex justify-content-around align-items-center p-3 border-top bg-white" }
const _hoisted_11 = { class: "input-title fw-bold m-0" }

import { computed, nextTick, onMounted, ref, watch } from "vue";
import { DatePicker as VDatePicker } from "v-calendar";
import { onClickOutside } from "@vueuse/core";
import { useStore } from 'vuex';
import { format } from 'date-fns';
import * as _ from "lodash";

// Local imports
import {
  parseDateString,
  formatDateToString,
  formatDateRange,
} from "@/vue/planning/utils/datePickerHelpers";
import { AttributesRef, DateInfo, RangeType } from "@/types/datePicker";
import {
  getLanguageCode,
  parseHashParams,
  getSasonBasedOnDate,
  formatDateForHash,
  isMobile as checkMobile,
} from "@/utils/utils";
import { HashParams } from "@/types";
import { airportCodeMap } from "@/config/airportCodeMap";
import ClsBtn from '@/vue/common/clsBtn.vue';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface Props {
  show?: boolean;
}

interface Emit {
  (e: "close"): void;
}

interface DatePickerPages {
  month: number;
  year: number;
}

interface SearchParams {
  locale: string;
  packCode: string;
  liheId: string;
  month?: string;
  origin?: string;
  startDate?: string;
  season?: string;
}

// ============================================================================
// Props & Emits
// ============================================================================

const SELECTED_COLOR = "blue";
const DATE_FETCH_DEBOUNCE_MS = 500;
const MONTHS_TO_FETCH_COUNT = 5; // 2 before, current, 2 after
const DATE_RANGE_YEARS = 2; // Years before/after current year for disabled dates

// ============================================================================
// Reactive State - Date Management
// ============================================================================


export default /*@__PURE__*/_defineComponent({
  __name: 'DatePickerV2',
  props: {
    show: { type: Boolean, default: false }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// ============================================================================
// Store & Initialization
// ============================================================================

const store = useStore();

// ============================================================================
// Constants
// ============================================================================

const startDate = ref<string | null>(store.state.dates["BeginDate"]);
const endDate = ref<string | null>(store.state.dates["EndDate"]);
const selectedDate = ref<string | null>(startDate.value);
const prevSelectedDate = ref<Date | null>(null);
const tempPrevSelectedDate = ref<Date | null>(null);
const availableDates = ref<Date[]>([]);
const currentMonth = ref<string>("");
const currentYear = ref<string>("");
const dateRange = ref<RangeType>({ start: "", end: "" });
const originalStartDate = ref<string | null>(null);
const originalEndDate = ref<string | null>(null);
const datePickerKey = ref<number>(0);

// ============================================================================
// Reactive State - UI State
// ============================================================================

const isMobile = ref<boolean>(false);
const isOpened = ref<boolean>(props.show);
const isSelectingEndDate = ref<boolean>(false);
const isFetchingDates = ref<boolean>(false);
const isClosing = ref<boolean>(false);
const justCompletedSelection = ref<boolean>(false);
const fetchedStartMonths = new Set<string>();
// Start dates stay here. availableDates is swapped to end dates while a return
// date is being chosen, then restored from this cache.
const cachedStartDates = ref<Date[]>([]);

// ============================================================================
// Reactive State - Refs
// ============================================================================

const datePickerContainerRef = ref<HTMLElement | null>(null);
const datePickerRef = ref<InstanceType<typeof VDatePicker> | null>(null);

// ============================================================================
// Reactive State - Configuration
// ============================================================================

const locale = ref(getLanguageCode(store.getters.locale));
const selectedColor = ref(SELECTED_COLOR);

const dateAttributes = ref<AttributesRef[]>([
  {
    key: "selected-dates",
    highlight: SELECTED_COLOR,
    dates: { start: new Date(), end: new Date() },
  },
]);

// ============================================================================
// Reactive State - API Parameters
// ============================================================================

const searchParams = ref<SearchParams>({
  locale: store.getters.locale,
  packCode: store.state.packCode || '',
  liheId: window.lihe.id,
});

const seasonParams = ref({
  season: "",
});

const monthParams = ref<Record<string, string>>({
  month: "",
});

// ============================================================================
// Computed Properties
// ============================================================================

const content = computed(() => store.state.content);

const flightOrigin = computed<string>(() => {
  const origin = store.state.flightOrigin;
  const noneAirport = airportCodeMap.get("None")?.name;
  
  if (origin && origin !== noneAirport) {
    return origin;
  }
  return "";
});

const departureText = computed((): string => {
  if (!startDate.value) return "";
  return formatDateRange(store.getters.locale, startDate.value);
});

const returnText = computed((): string => {
  if (!endDate.value) return "";
  return formatDateRange(store.getters.locale, endDate.value);
});

const dateRangeLabel = computed((): string => {
  if (startDate.value && endDate.value && departureText.value && returnText.value) {
    return `${departureText.value} - ${returnText.value}`;
  }
  return content.value.planner_datePicker_depart_return;
});

const disabledDates = computed(() => generateDisabledDates(availableDates.value));

// ============================================================================
// Methods - Date Selection & Search
// ============================================================================

/** Today at midnight – used as min-date so past dates are disabled and past months cannot be navigated to */
const minSelectableDate = computed(() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
});

/** Today at midnight – used as max-date so future dates up to 18 months from today are disabled and future months cannot be navigated to */
const maxSelectableDate = computed(() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() + 18); // 18 months from today
  return d;
});

/**
 * Handles the complete date range selection and triggers search
 */
const handleSearch = async (departureDate: Date, returnDate: Date): Promise<void> => {
  if (!(departureDate instanceof Date) || !(returnDate instanceof Date)) {
    console.error('handleSearch: Invalid date parameters');
    return;
  }

  // Format new dates
  const newStartDate = formatDateToString(departureDate);
  const newEndDate = formatDateToString(returnDate);
  
  // Get current dates from store before updating
  const currentStartDate = store.state.dates["BeginDate"];
  const currentEndDate = store.state.dates["EndDate"];
  
  // Check if dates have actually changed
  const datesChanged = newStartDate !== currentStartDate || newEndDate !== currentEndDate;

  // Update local state
  startDate.value = newStartDate;
  endDate.value = newEndDate;
  
  // Update store
  store.dispatch("setDates", {
    BeginDate: startDate.value,
    EndDate: endDate.value,
  });

  // Reset selection mode
  isSelectingEndDate.value = false;

  // Update range and attributes
  dateRange.value = {
    start: startDate.value!,
    end: endDate.value!,
  };

  dateAttributes.value[0].dates = {
    start: departureDate,
    end: returnDate,
  };

  // Only reset WiypCall if dates have actually changed
  if (datesChanged) {
    store.dispatch("resetWiypCall");
  }
  // Close picker first (skip reset since we've already handled the state)
  // The justCompletedSelection flag should already be set by completeEndDateSelection
  await toggleDatePicker(false, true);
  
  // Wait for DOM to update
  await nextTick();
  
  // Restore start dates around the new departure. End-date selection replaced
  // availableDates, and those months may already be cached.
  const month = String(departureDate.getUTCMonth() + 1).padStart(2, "0");
  currentMonth.value = month;
  currentYear.value = String(departureDate.getUTCFullYear());
  await fetchStartDates(month);
  
  // Reset the flag after a delay to allow click events to settle
  setTimeout(() => {
    justCompletedSelection.value = false;
  }, 300);
};

/**
 * Handles date selection from the calendar
 */
const handleDateSelection = async (dateInfo: DateInfo): Promise<void> => {
  if (dateInfo.isDisabled) return;

  // Handle season switching if needed
  await handleSeasonSwitch(dateInfo);

  // If selecting end date, complete the selection
  if (isSelectingEndDate.value) {
    prevSelectedDate.value = tempPrevSelectedDate.value;
    await completeEndDateSelection(dateInfo);
    return;
  }

  // Otherwise, start selecting departure date
  await startDepartureDateSelection(dateInfo);
};

/**
 * Handles season switching based on selected date
 */
const handleSeasonSwitch = async (dateInfo: DateInfo): Promise<void> => {
  const formattedDate = formatDateForHash(dateInfo.id, true);
  const seasonBasedOnDate = getSasonBasedOnDate(formattedDate);

  if (seasonBasedOnDate !== store.state.season) {
    store.commit("setSeason", seasonBasedOnDate);
    window.seasonManager.switchSeasonWithoutReloading();
    seasonParams.value.season = seasonBasedOnDate;
  }
};

/**
 * Completes the end date selection
 */
const completeEndDateSelection = async (dateInfo: DateInfo): Promise<void> => {
  const returnDate = parseDateString(dateInfo.id);
  
  if (!prevSelectedDate.value || !returnDate) {
    return;
  }

  // Set flag before search to prevent any click events from reopening
  justCompletedSelection.value = true;
  
  await handleSearch(prevSelectedDate.value, returnDate);
};

/**
 * Starts the departure date selection process
 */
const startDepartureDateSelection = async (dateInfo: DateInfo): Promise<void> => {
  // Enter end date selection mode
  isSelectingEndDate.value = true;

  // Parse and store the departure date
  const departureDate = parseDateString(dateInfo.id);
  if (!departureDate) return;
  
  //prevSelectedDate.value = departureDate;
  tempPrevSelectedDate.value = departureDate;
  selectedDate.value = dateInfo.id;

  // Prepare parameters for fetching end dates
  monthParams.value.month = extractMonthFromDateId(dateInfo.id);

  if (flightOrigin.value && store.state.transportationSelected) {
    monthParams.value.origin = flightOrigin.value;
  }

  // Fetch available end dates
  try {
    const endDates = await window.searchEngineService.getRelatedPackages(
      {
        ...searchParams.value,
        ...monthParams.value,
        startDate: dateInfo.id,
      },
      "getEndDates"
    );

    // Jump to first available end date if not in view
    if (endDates[0] && !isDateInView(endDates[0])) {
      await jumpToDate(new Date(endDates[0]));
    }

    // Update available dates and clear attributes
    dateAttributes.value[0].dates = null;
    availableDates.value = formatDatesArray(endDates);
  } catch (error) {
    console.error("Error fetching end dates:", error);
  }
};

// ============================================================================
// Methods - Date Picker UI Control
// ============================================================================

/**
 * Toggles the date picker visibility
 */
const handleToggleDatePicker = (event?: Event): void => {
  // Stop event propagation if it came from calendar
  if (event) {
    const target = event.target as HTMLElement;
    const calendarContainer = document.querySelector(".datePickerContainerRef");
    const mobileContainer = document.querySelector(".mobile-datepicker-popup");
    
    // If click came from within the calendar container, stop it
    if (calendarContainer?.contains(target) || mobileContainer?.contains(target)) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
  }
  
  // If we just completed a selection, block any toggle for a short period
  if (justCompletedSelection.value) {
    return;
  }
  
  const shouldShow = isMobile.value ? false : !isOpened.value;
  
  // Only prevent closing if we're in the process of closing
  if (!shouldShow && isClosing.value) return;
  
  toggleDatePicker(shouldShow);
};

/**
 * Opens or closes the date picker
 */
const toggleDatePicker = async (show?: boolean, skipReset = false): Promise<void> => {
  const pickerDiv = document.getElementById("datePickerDiv") as HTMLElement | null;

  // Determine if we should show or hide
  if (typeof show === "undefined") {
    show = isMobile.value ? false : !isOpened.value;
  }

  // Prevent closing if we're already in the process of closing (unless skipReset is true)
  if (!show && isClosing.value && !skipReset) {
    return;
  }

  if (show) {
    // Reset flags when opening
    isClosing.value = false;
    // Don't reset justCompletedSelection here - let the timeout handle it
    
    // For mobile, set isOpened first so the popup renders, then open
    if (isMobile.value) {
      isOpened.value = true;
      await nextTick(); // Wait for mobile popup to render
    }
    
    await openDatePicker(pickerDiv);
  } else {
    // Set closing flag to prevent immediate reopening
    isClosing.value = true;
    closeDatePicker(pickerDiv, skipReset);
    // Reset closing flag after a short delay
    setTimeout(() => {
      isClosing.value = false;
    }, 100);
  }

  // Set isOpened for desktop (mobile already set above)
  if (!isMobile.value || !show) {
    isOpened.value = show;
  }
};

/**
 * Opens the date picker
 */
const openDatePicker = async (pickerDiv: HTMLElement | null): Promise<void> => {
  if (isMobile.value) {
    document.body.style.overflow = 'hidden';
  }

  // For mobile, wait for popup to be fully rendered
  if (isMobile.value) {
    await nextTick();
    // Additional wait to ensure VDatePicker is mounted
    await nextTick();
  }

  if (!pickerDiv || (!isMobile.value && !datePickerContainerRef.value)) return;

  // Save original dates from store before any new selection
  originalStartDate.value = store.state.dates["BeginDate"];
  originalEndDate.value = store.state.dates["EndDate"];

  // Ensure local state is in sync with store (in case watchers haven't updated yet)
  startDate.value = originalStartDate.value;
  endDate.value = originalEndDate.value;

  // Reset selection state when opening
  isSelectingEndDate.value = false;
  prevSelectedDate.value = null;
  tempPrevSelectedDate.value = null;
  
  // Reset selectedDate to original start date - this ensures VDatePicker shows correct date
  selectedDate.value = originalStartDate.value;
  
  // Wait for next tick to ensure VDatePicker updates
  await nextTick();

  // Extract month and year from startDate to ensure calendar shows correct period
  if (originalStartDate.value) {
    const beginDateParts = originalStartDate.value.split("/");
    const monthFromBeginDate = beginDateParts[1];
    const yearFromBeginDate = beginDateParts[2];
    
    // Update current month and year
    currentMonth.value = monthFromBeginDate;
    currentYear.value = yearFromBeginDate;
    
    // Fetch start dates for the correct period
    await fetchStartDates(currentMonth.value);
    
    // Move calendar to the correct period (especially important for mobile)
    // Wait one more tick for mobile to ensure calendar is ready
    if (isMobile.value) {
      await nextTick();
    }
    await moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  } else if (currentMonth.value && currentYear.value) {
    // Fallback: use existing currentMonth/currentYear if startDate is not available
    await fetchStartDates(currentMonth.value);
  }

  // Reset date attributes to show current range (use original dates)
  if (originalStartDate.value && originalEndDate.value) {
    dateAttributes.value[0].dates = {
      start: parseDateString(originalStartDate.value)!,
      end: parseDateString(originalEndDate.value)!,
    };
  } else {
    dateAttributes.value[0].dates = null;
  }

  setTimeout(() => {
    document.addEventListener("click", handleOutsideClick);
    if (pickerDiv) {
      pickerDiv.style.color = "var(--primary-blue)";
    }
  }, 1);
};

/**
 * Closes the date picker
 */
const closeDatePicker = (pickerDiv?: HTMLElement | null, skipReset = false): void => {
  const pickerDivElement = pickerDiv || document.getElementById("datePickerDiv") as HTMLElement | null;

  // If user selected a start date but didn't select an end date, restore original dates
  // Check both prevSelectedDate and tempPrevSelectedDate since tempPrevSelectedDate is set when selecting start date
  if (!skipReset && isSelectingEndDate.value && (prevSelectedDate.value || tempPrevSelectedDate.value)) {
    // Reset selection state first
    isSelectingEndDate.value = false;
    prevSelectedDate.value = null;
    tempPrevSelectedDate.value = null;
    
    // Restore original dates from when picker was opened
    startDate.value = originalStartDate.value;
    endDate.value = originalEndDate.value;
    
    // Update store with original dates
    store.dispatch("setDates", {
      BeginDate: originalStartDate.value,
      EndDate: originalEndDate.value,
    });
    
    // Update dateRange to reflect original dates
    dateRange.value = {
      start: originalStartDate.value || "",
      end: originalEndDate.value || "",
    };
    
    // IMPORTANT: Set selectedDate FIRST to ensure VDatePicker shows correct date
    // This must happen before updating date attributes
    selectedDate.value = originalStartDate.value;
    
    // Increment key to force VDatePicker re-render with correct date
    datePickerKey.value += 1;
    
    // Clear date attributes first to remove visual selection from orphan date
    dateAttributes.value[0].dates = null;
    
    // Then set date attributes if we have a complete date range
    if (originalStartDate.value && originalEndDate.value) {
      dateAttributes.value[0].dates = {
        start: parseDateString(originalStartDate.value)!,
        end: parseDateString(originalEndDate.value)!,
      };
    }
  } else if (!skipReset && isSelectingEndDate.value) {
    // Reset selection state even if prevSelectedDate is null
    // Also reset tempPrevSelectedDate to ensure clean state
    tempPrevSelectedDate.value = null;
    
    // Reset selectedDate to original start date
    selectedDate.value = originalStartDate.value;
    
    // Increment key to force VDatePicker re-render with correct date
    datePickerKey.value += 1;
    
    resetDateSelection();
  }

  // Restore body overflow on mobile
  if (isMobile.value) {
    document.body.style.overflow = 'auto';
  }

  if (!pickerDivElement || !datePickerContainerRef.value) return;

  document.removeEventListener("click", handleOutsideClick);
  pickerDivElement.style.color = "black";
  emit("close");
};

/**
 * Resets the date selection state
 */
const resetDateSelection = async (): Promise<void> => {
  isSelectingEndDate.value = false;
  selectedDate.value = startDate.value;
  prevSelectedDate.value = null;
  tempPrevSelectedDate.value = null;

  // Reset available dates to show start dates
  if (currentMonth.value && currentYear.value) {
    await fetchStartDates(currentMonth.value);
  }

  // Reset date attributes
  if (startDate.value && endDate.value) {
    dateAttributes.value[0].dates = {
      start: parseDateString(startDate.value)!,
      end: parseDateString(endDate.value)!,
    };
  } else {
    dateAttributes.value[0].dates = null;
  }
};

/**
 * Handles clicks outside the date picker
 */
const handleOutsideClick = (event: MouseEvent): void => {
  const isDatePickerContainer = document.querySelector(".datePickerContainerRef")?.contains(event.target as Node);
  const isShowDatePickerBtn = document.querySelector(".showDatePickerBtn")?.contains(event.target as Node);
  
  if (event.target && !isDatePickerContainer && !isShowDatePickerBtn) {
    toggleDatePicker(false);
  }
};

/**
 * Handles mobile popup backdrop clicks
 */
const handleMobilePopupClick = (event: MouseEvent): void => {
  if (event.target === event.currentTarget) {
    toggleDatePicker(false);
  }
};

// ============================================================================
// Methods - Date Fetching & Management
// ============================================================================

const getStartMonthFetchKey = (yearMonth: string, packCode: string): string => {
  const origin =
    flightOrigin.value && store.state.transportationSelected
      ? flightOrigin.value
      : "";
  return `${searchParams.value.liheId}|${packCode}|${yearMonth}|${origin}`;
};

const showCachedStartDates = (): void => {
  if (isSelectingEndDate.value) return;
  availableDates.value = [...cachedStartDates.value];
};

const resetFetchedStartDates = (): void => {
  fetchedStartMonths.clear();
  cachedStartDates.value = [];
  availableDates.value = [];
};

const mergeDates = (current: Date[], incoming: Date[]): Date[] => {
  return Array.from(
    new Map(
      [...current, ...incoming].map(date => [date.getTime(), date])
    ).values()
  );
};

/**
 * Fetches start dates for the specified month and surrounding months
 */
const fetchStartDates = async (
  monthToFetch: string,
): Promise<void> => {
  if (!currentYear.value || !monthToFetch) return;

  // Keep searchParams in sync — packCode may still be empty during early init
  const packCode = store.state.packCode;
  if (!packCode) return;
  searchParams.value.packCode = packCode;

  const origin =
    flightOrigin.value && store.state.transportationSelected
      ? flightOrigin.value
      : undefined;
  if (origin) {
    monthParams.value.origin = origin;
  }

  // Put start dates back as soon as we are not choosing a return date.
  // A completed stay leaves availableDates equal to the end-date list, and
  // months already in fetchedStartMonths would otherwise never be restored.
  showCachedStartDates();

  const monthsToFetch = getMonthsToFetch(monthToFetch, currentYear.value).filter(
    (yearMonth) => !fetchedStartMonths.has(getStartMonthFetchKey(yearMonth, packCode))
  );

  if (monthsToFetch.length === 0) return;

  monthsToFetch.forEach((yearMonth) => {
    fetchedStartMonths.add(getStartMonthFetchKey(yearMonth, packCode));
  });

  try {
    const startDatesPromises = monthsToFetch.map((yearMonth) =>
      window.searchEngineService.getStartDates({
        ...searchParams.value,
        month: yearMonth,
        ...(origin && { origin }),
      })
    );

    const resolvedStartDates = await Promise.all(startDatesPromises);
    const allStartDates = resolvedStartDates.flatMap(formatDatesArray);

    cachedStartDates.value = mergeDates(cachedStartDates.value, allStartDates);
    showCachedStartDates();
  } catch (error) {
    monthsToFetch.forEach((yearMonth) => {
      fetchedStartMonths.delete(getStartMonthFetchKey(yearMonth, packCode));
    });
    console.error("Error fetching start dates:", error);
  }
};

/**
 * Gets YYYY-MM keys for 2 months before, current, and 2 after the center month
 */
const getMonthsToFetch = (centerMonth: string, year: string): string[] => {
  const center = new Date(Number(year), Number(centerMonth) - 1, 1);
  const radius = Math.floor(MONTHS_TO_FETCH_COUNT / 2);
  const months: string[] = [];

  for (let offset = -radius; offset <= radius; offset++) {
    const date = new Date(center.getFullYear(), center.getMonth() + offset, 1);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    months.push(`${date.getFullYear()}-${month}`);
  }

  return months;
};

/**
 * Handles calendar page changes
 */
const handlePagesChange = async (pages: DatePickerPages[]): Promise<void> => {
  const firstPage = pages[0];
  const formattedMonth = firstPage.month.toString().padStart(2, "0");
  const formattedYear = firstPage.year.toString();

  const isSameMonthYear = 
    currentMonth.value === formattedMonth && 
    currentYear.value === formattedYear;

  if (isSameMonthYear || isFetchingDates.value || isSelectingEndDate.value) {
    return;
  }

  currentMonth.value = formattedMonth;
  currentYear.value = formattedYear;

  // Debounce fetching
  isFetchingDates.value = true;
  setTimeout(() => {
    isFetchingDates.value = false;
  }, DATE_FETCH_DEBOUNCE_MS);

  await fetchStartDates(currentMonth.value);
};

/**
 * Formats an array of date strings to Date objects
 */
const formatDatesArray = (dateStrings: string[]): Date[] => {
  return dateStrings
    .map((dateString) => parseDateString(dateString))
    .filter((date): date is Date => date !== null);
};

/**
 * Generates disabled dates based on available dates
 */
const generateDisabledDates = (available: Date[]): Date[] => {
  const result: Date[] = [];
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  startDate.setFullYear(today.getFullYear() - DATE_RANGE_YEARS);
  endDate.setFullYear(today.getFullYear() + DATE_RANGE_YEARS);

  const availableSet = new Set(
    available.map(date => format(date, 'yyyy-MM-dd'))
  );

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dayStr = format(d, 'yyyy-MM-dd');
    if (!availableSet.has(dayStr)) {
      result.push(new Date(d));
    }
  }

  return result;
};

/**
 * Checks if a date is currently visible in the calendar view
 */
const isDateInView = (dateString: string): boolean => {
  const selector = `${isMobile.value ? '.mobile-datepicker-popup' : ''} [class*="vc-day id-${dateString}"].in-month`;
  const dateElement = document.querySelector(selector);
  return dateElement !== null;
};

/**
 * Jumps the calendar to a specific date
 */
const jumpToDate = async (targetDate: Date): Promise<void> => {
  if (!datePickerRef.value) return;

  await datePickerRef.value.move({
    month: targetDate.getMonth() + 1,
    year: targetDate.getFullYear(),
  });
};

/**
 * Moves the calendar to a specific month and year
 */
const moveDatePickerMonth = async (month: string, year: string): Promise<void> => {
  await datePickerRef.value?.move({
    month: +month,
    year: +year,
  });
};

/**
 * Extracts month string from date ID (YYYY-MM-DD format)
 */
const extractMonthFromDateId = (dateId: string): string => {
  return dateId.split("-").slice(0, 2).join("-");
};

/**
 * Initializes the date picker with existing data
 */
const initializeDatePicker = async (): Promise<void> => {
  if (!startDate.value || !endDate.value || !dateAttributes.value[0].dates) {
    return;
  }

  // Set initial date attributes
  dateAttributes.value[0].dates.start = parseDateString(startDate.value)!;
  dateAttributes.value[0].dates.end = parseDateString(endDate.value)!;

  // Set initial range
  dateRange.value = {
    start: store.state.dates["BeginDate"],
    end: store.state.dates["EndDate"],
  };

  // Extract month and year from begin date
  const beginDateParts = store.state.dates["BeginDate"].split("/");
  const monthFromBeginDate = beginDateParts[1];
  const yearFromBeginDate = beginDateParts[2];

  currentMonth.value = monthFromBeginDate;
  currentYear.value = yearFromBeginDate;

  // Fetch start dates and move calendar
  await fetchStartDates(currentMonth.value);
  await moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
};

// ============================================================================
// Lifecycle Hooks
// ============================================================================

onMounted(() => {
  isMobile.value = checkMobile();
  
  if (!isMobile.value) {
    onClickOutside(datePickerContainerRef, () => {
      if (!isClosing.value) {
        toggleDatePicker(false);
      }
    });
  }
});

// ============================================================================
// Watchers
// ============================================================================

// Watch for prop changes
watch(() => props.show, (newValue) => {
  // Allow opening even if closing, but prevent closing if already closing
  if (newValue || !isClosing.value) {
    toggleDatePicker(newValue);
  }
});

// Watch for packCode changes
watch(
  () => store.state.packCode,
  async (newPackCode) => {
    if (!newPackCode) return;

    const hashParams = parseHashParams() as HashParams;

    searchParams.value.packCode = newPackCode;
    seasonParams.value.season = hashParams.season;
    resetFetchedStartDates();

    toggleDatePicker(false);

    const beginDateParts = store.state.dates["BeginDate"]?.split("/") || [];
    const monthFromBeginDate = beginDateParts[1];
    const yearFromBeginDate = beginDateParts[2];

    if (monthFromBeginDate && yearFromBeginDate) {
      currentMonth.value = monthFromBeginDate;
      currentYear.value = yearFromBeginDate;
    }

    await fetchStartDates(currentMonth.value);
    if (monthFromBeginDate && yearFromBeginDate) {
      await moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
    }
  }
);

// Watch for participants changes
watch(
  () => store.state.participants,
  () => {
    toggleDatePicker(false);
  }
);

// Watch for BeginDate changes
watch(
  () => store.state.dates["BeginDate"],
  (newBeginDate) => {
    startDate.value = newBeginDate;
    // Update dateRange to keep it in sync
    if (newBeginDate && endDate.value) {
      dateRange.value = {
        start: newBeginDate,
        end: endDate.value,
      };
    }
  }
);

// Watch for EndDate changes
watch(
  () => store.state.dates["EndDate"],
  (newEndDate) => {
    endDate.value = newEndDate;
    // Update dateRange to keep it in sync
    if (startDate.value && newEndDate) {
      dateRange.value = {
        start: startDate.value,
        end: newEndDate,
      };
    }
  }
);

// Watch for loading state to initialize — only once data is ready
watch(
  () => store.state.loading,
  (isLoading) => {
    if (!isLoading) {
      initializeDatePicker();
    }
  }
);


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      id: "datePickerDiv",
      class: "w-100 d-flex justify-content-center align-items-start p-3 text-dark",
      onClick: _cache[0] || (_cache[0] = (event) => handleToggleDatePicker(event))
    }, [
      _createElementVNode("div", _hoisted_1, [
        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "me-2 align-self-center" }, [
          _createElementVNode("svg", { class: "icon tf-icon-22 text-blue-900" }, [
            _createElementVNode("use", { "xlink:href": "#___icon___calander" })
          ])
        ], -1)),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", _hoisted_3, _toDisplayString(dateRangeLabel.value), 1)
        ])
      ])
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["datePickerContainerRef datepicker-input position-absolute", [isOpened.value ? 'd-block' : 'd-none']]),
      ref_key: "datePickerContainerRef",
      ref: datePickerContainerRef,
      onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
    }, [
      (!isMobile.value)
        ? (_openBlock(), _createBlock(_unref(VDatePicker), {
            modelValue: selectedDate.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedDate).value = $event)),
            key: `datepicker-${datePickerKey.value}`,
            locale: locale.value,
            ref_key: "datePickerRef",
            ref: datePickerRef,
            columns: 2,
            rows: 1,
            "first-day-of-week": 2,
            color: selectedColor.value,
            attributes: dateAttributes.value,
            "available-dates": availableDates.value,
            "min-date": minSelectableDate.value,
            "max-date": maxSelectableDate.value,
            mode: "date",
            "disabled-dates": disabledDates.value,
            onDayclick: handleDateSelection,
            "onUpdate:pages": handlePagesChange,
            step: 1
          }, {
            footer: _withCtx(() => [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("label", _hoisted_5, _toDisplayString(dateRangeLabel.value), 1)
              ])
            ]),
            _: 1
          }, 8, ["modelValue", "locale", "color", "attributes", "available-dates", "min-date", "max-date", "disabled-dates"]))
        : _createCommentVNode("", true)
    ], 2),
    (isMobile.value && isOpened.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "mobile-datepicker-popup",
          onClick: _withModifiers(handleMobilePopupClick, ["stop"])
        }, [
          _createElementVNode("div", {
            class: "mobile-datepicker-content bg-white rounded-4 w-100 h-100 overflow-auto position-relative d-flex flex-column align-items-center",
            onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(ClsBtn, { onClick: closeDatePicker })
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              (_openBlock(), _createBlock(_unref(VDatePicker), {
                modelValue: selectedDate.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedDate).value = $event)),
                key: `datepicker-mobile-${datePickerKey.value}`,
                locale: locale.value,
                ref_key: "datePickerRef",
                ref: datePickerRef,
                columns: 1,
                rows: 6,
                "first-day-of-week": 2,
                color: selectedColor.value,
                attributes: dateAttributes.value,
                "available-dates": availableDates.value,
                "min-date": minSelectableDate.value,
                "max-date": maxSelectableDate.value,
                mode: "date",
                "disabled-dates": disabledDates.value,
                onDayclick: handleDateSelection,
                "onUpdate:pages": handlePagesChange
              }, null, 8, ["modelValue", "locale", "color", "attributes", "available-dates", "min-date", "max-date", "disabled-dates"]))
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("label", _hoisted_11, _toDisplayString(dateRangeLabel.value), 1)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})